/**
 * Vehicle sale-record PDF export.
 *
 * Two halves:
 *  - Pure data assembler + paywall gate (unit-tested in sale-export.test.ts)
 *  - Browser-only PDF renderer using jsPDF (visual, not unit-tested)
 */
import type { Vehicle, ServiceLogEntry } from './api';
import type { Me } from './user-prefs';
import type { MaintenanceScheduleEntry, RecallNotice } from '@cartrack/shared';
import { unitSuffix } from '@cartrack/shared';

// ---------- Paywall gate ----------

export interface CanExportInputs {
  plan: string;
  paywallEnabled: boolean;
}

export interface CanExportResult {
  enabled: boolean;
  reason: string | null;
}

export function canExportSaleRecord({ plan, paywallEnabled }: CanExportInputs): CanExportResult {
  if (!paywallEnabled) return { enabled: true, reason: null };
  if (plan === 'free') {
    return { enabled: false, reason: 'Upgrade to premium to download sale records' };
  }
  return { enabled: true, reason: null };
}

/** Reads the client env var that gates the export. Defaults to false. */
export function isPaywallEnabled(): boolean {
  return import.meta.env.VITE_PAYWALL_EXPORT === 'true';
}

// ---------- Data assembler ----------

export interface SaleRecordInputs {
  vehicle: Vehicle;
  logs: ServiceLogEntry[];
  schedule: MaintenanceScheduleEntry[];
  recalls: RecallNotice[];
  me: Me;
  now: Date;
}

export interface SaleRecord {
  generatedAt: string;
  vehicle: Vehicle;
  owner: {
    email: string;
    displayName: string | null;
  };
  logs: ServiceLogEntry[];
  logCount: number;
  totalSpend: number;
  schedule: MaintenanceScheduleEntry[];
  recalls: RecallNotice[];
}

export function assembleSaleRecord(inputs: SaleRecordInputs): SaleRecord {
  const { vehicle, logs, schedule, recalls, me, now } = inputs;

  const sortedLogs = [...logs].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );

  const totalSpend = sortedLogs.reduce((sum, log) => {
    return sum + (typeof log.cost === 'number' ? log.cost : 0);
  }, 0);

  // Only show schedule entries that are actually relevant (have a next-due
  // anchor). A zero-interval "battery check by date" entry is kept if it has
  // a nextDate; baseline-derived entries without either are noise.
  const relevantSchedule = schedule.filter(
    (s) => s.nextMileage !== null || s.nextDate !== null,
  );

  // Active recalls only — NHTSA rows without a campaign number are upstream
  // noise and shouldn't appear on a sale record.
  const activeRecalls = recalls.filter(
    (r) => r.campaignNumber !== null && r.campaignNumber !== '' && r.campaignNumber !== undefined,
  );

  return {
    generatedAt: now.toISOString(),
    vehicle,
    owner: {
      email: me.email,
      displayName: me.displayName,
    },
    logs: sortedLogs,
    logCount: sortedLogs.length,
    totalSpend: Math.round(totalSpend * 100) / 100,
    schedule: relevantSchedule,
    recalls: activeRecalls,
  };
}

// ---------- Recall source labeling ----------

const RECALLS_HEADING_NHTSA = 'Active recalls (NHTSA)';
const RECALLS_HEADING_TC = 'Active recalls (Transport Canada)';
const RECALLS_NOTE_NHTSA =
  'This record is owner-maintained via CarTrack Pro. NHTSA recall data is public and unverified by CarTrack Pro.';
const RECALLS_NOTE_TC =
  'This record is owner-maintained via CarTrack Pro. Transport Canada recall data is public and unverified by CarTrack Pro.';

/** True if any recall being rendered came from Transport Canada rather than NHTSA. */
function hasTransportCanadaRecall(recalls: RecallNotice[]): boolean {
  return recalls.some((r) => r.source === 'TC');
}

/** Section heading for the recalls table, matching the regulator that sourced them. */
export function recallSectionHeading(recalls: RecallNotice[]): string {
  return hasTransportCanadaRecall(recalls) ? RECALLS_HEADING_TC : RECALLS_HEADING_NHTSA;
}

/** Footer disclaimer, matching the regulator that sourced the rendered recalls. */
export function recallSourceNote(recalls: RecallNotice[]): string {
  return hasTransportCanadaRecall(recalls) ? RECALLS_NOTE_TC : RECALLS_NOTE_NHTSA;
}

// ---------- PDF renderer (browser only) ----------

export interface RenderOptions {
  unitSystem: 'metric' | 'imperial';
}

/**
 * Build a multi-page PDF and trigger download. Dynamic-imports jspdf so it
 * doesn't bloat the bundle for users who never click export.
 */
export async function renderSaleRecordPdf(
  record: SaleRecord,
  opts: RenderOptions,
): Promise<void> {
  const [{ default: jsPDF }, autoTable] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable').then((m) => m.default),
  ]);

  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const unit = unitSuffix(opts.unitSystem);
  const margin = 48;
  let y = margin;

  // Header
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('Vehicle Service Record', margin, y);
  y += 22;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100);
  doc.text(
    `Generated by CarTrack Pro · ${new Date(record.generatedAt).toLocaleString()}`,
    margin,
    y,
  );
  y += 24;
  doc.setTextColor(0);

  // Vehicle section
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Vehicle', margin, y);
  y += 16;
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  const vLines = [
    `${record.vehicle.year} ${record.vehicle.make} ${record.vehicle.model}${record.vehicle.trim ? ` · ${record.vehicle.trim}` : ''}`,
    `VIN: ${record.vehicle.vin ?? '—'}`,
    `Engine: ${record.vehicle.engine ?? '—'}`,
    `Odometer: ${record.vehicle.currentMileage.toLocaleString()} ${unit} (as of ${new Date(record.vehicle.mileageUpdatedAt).toLocaleDateString()})`,
    `Garage: ${record.vehicle.garageCountry}${record.vehicle.garageStateProvince ? `, ${record.vehicle.garageStateProvince}` : ''}${record.vehicle.garagePostalCode ? ` ${record.vehicle.garagePostalCode}` : ''}`,
    `Owner: ${record.owner.displayName ?? record.owner.email}`,
  ];
  for (const line of vLines) {
    doc.text(line, margin, y);
    y += 14;
  }
  y += 8;

  // Summary
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Service summary', margin, y);
  y += 16;
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(`${record.logCount} logged services`, margin, y);
  y += 14;
  doc.text(`Total recorded spend: $${record.totalSpend.toFixed(2)}`, margin, y);
  y += 22;

  // Service history table
  if (record.logs.length > 0) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Service history', margin, y);
    y += 6;
    autoTable(doc, {
      startY: y + 4,
      head: [['Date', 'Odometer', 'Service', 'Shop / mechanic', 'Cost', 'Notes']],
      body: record.logs.map((l) => [
        new Date(l.date).toLocaleDateString(),
        `${l.mileage.toLocaleString()} ${unit}`,
        l.serviceType,
        [l.shop, l.mechanic].filter(Boolean).join(' / ') || '—',
        l.cost !== null ? `$${Number(l.cost).toFixed(2)}` : '—',
        l.notes ?? '',
      ]),
      margin: { left: margin, right: margin },
      styles: { fontSize: 9, cellPadding: 4 },
      headStyles: { fillColor: [30, 64, 175], textColor: 255 },
      columnStyles: {
        0: { cellWidth: 70 },
        1: { cellWidth: 60 },
        4: { cellWidth: 55, halign: 'right' },
      },
    });
    y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 24;
  }

  // Schedule
  if (record.schedule.length > 0) {
    if (y > 700) {
      doc.addPage();
      y = margin;
    }
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Upcoming maintenance', margin, y);
    autoTable(doc, {
      startY: y + 10,
      head: [['Service', 'Status', 'Next due (odometer)', 'Next due (date)']],
      body: record.schedule.map((s) => [
        s.serviceType,
        s.status,
        s.nextMileage !== null ? `${s.nextMileage.toLocaleString()} ${unit}` : '—',
        s.nextDate ? new Date(s.nextDate).toLocaleDateString() : '—',
      ]),
      margin: { left: margin, right: margin },
      styles: { fontSize: 9, cellPadding: 4 },
      headStyles: { fillColor: [30, 64, 175], textColor: 255 },
    });
    y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 24;
  }

  // Recalls
  if (record.recalls.length > 0) {
    if (y > 700) {
      doc.addPage();
      y = margin;
    }
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(recallSectionHeading(record.recalls), margin, y);
    autoTable(doc, {
      startY: y + 10,
      head: [['Campaign', 'Component', 'Summary']],
      body: record.recalls.map((r) => [
        r.campaignNumber ?? '—',
        r.component ?? '—',
        r.summary ?? '',
      ]),
      margin: { left: margin, right: margin },
      styles: { fontSize: 9, cellPadding: 4 },
      headStyles: { fillColor: [153, 27, 27], textColor: 255 },
    });
  }

  // Footer disclaimer
  const pageCount = doc.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    doc.setFontSize(8);
    doc.setTextColor(140);
    doc.text(
      recallSourceNote(record.recalls),
      margin,
      doc.internal.pageSize.getHeight() - 24,
      { maxWidth: doc.internal.pageSize.getWidth() - 2 * margin },
    );
    doc.text(
      `Page ${p} of ${pageCount}`,
      doc.internal.pageSize.getWidth() - margin,
      doc.internal.pageSize.getHeight() - 24,
      { align: 'right' },
    );
  }

  const filename = `${record.vehicle.year}-${record.vehicle.make}-${record.vehicle.model}-record.pdf`
    .replace(/\s+/g, '-')
    .toLowerCase();
  doc.save(filename);
}
